import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import config from '../config'

interface User {
  email: string
  name?: string
}

interface MfaSetupData {
  secretCode: string
  email: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  // MFA states
  mfaRequired: boolean
  mfaSetupRequired: boolean
  mfaSetupData: MfaSetupData | null
  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  getToken: () => Promise<string | null>
  verifyMfaCode: (code: string) => Promise<void>
  completeMfaSetup: (code: string) => Promise<void>
  cancelMfa: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Initialize Cognito User Pool (only if configured)
const userPool = config.cognito.userPoolId
  ? new CognitoUserPool({
      UserPoolId: config.cognito.userPoolId,
      ClientId: config.cognito.clientId,
    })
  : null

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false)
  const [mfaSetupData, setMfaSetupData] = useState<MfaSetupData | null>(null)

  // Store cognitoUser for MFA verification flow
  const cognitoUserRef = useRef<CognitoUser | null>(null)

  useEffect(() => {
    // Check for existing session on mount
    checkSession()
  }, [])

  const checkSession = async () => {
    // If auth is disabled, set a dev user
    if (!config.features.authEnabled) {
      setUser({ email: 'dev@localhost', name: 'Developer' })
      setIsLoading(false)
      return
    }

    if (!userPool) {
      setIsLoading(false)
      return
    }

    const cognitoUser = userPool.getCurrentUser()
    if (!cognitoUser) {
      setIsLoading(false)
      return
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setUser(null)
        setIsLoading(false)
        return
      }

      // Get user attributes
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          console.error('Error getting user attributes:', err)
          setUser(null)
        } else {
          const email = attributes?.find((a) => a.Name === 'email')?.Value || ''
          const name = attributes?.find((a) => a.Name === 'name')?.Value
          setUser({ email, name })
        }
        setIsLoading(false)
      })
    })
  }

  const login = async (email: string, password: string): Promise<void> => {
    if (!userPool) {
      throw new Error('Authentication not configured')
    }

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      })

      // Store for MFA verification
      cognitoUserRef.current = cognitoUser

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      })

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (_session) => {
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              console.error('Error getting user attributes:', err)
            }
            const userEmail = attributes?.find((a) => a.Name === 'email')?.Value || email
            const name = attributes?.find((a) => a.Name === 'name')?.Value
            setUser({ email: userEmail, name })
            setMfaRequired(false)
            setMfaSetupRequired(false)
            resolve()
          })
        },
        onFailure: (err) => {
          cognitoUserRef.current = null
          reject(err)
        },
        newPasswordRequired: (_userAttributes) => {
          reject(new Error('New password required. Please contact administrator.'))
        },
        mfaSetup: (_challengeName, _challengeParameters) => {
          // User needs to set up TOTP for the first time
          cognitoUser.associateSoftwareToken({
            associateSecretCode: (secretCode: string) => {
              setMfaSetupData({ secretCode, email })
              setMfaSetupRequired(true)
              resolve() // Resolve to stop loading, UI will show setup screen
            },
            onFailure: (err) => {
              reject(err)
            },
          })
        },
        totpRequired: (_challengeName, _challengeParameters) => {
          // User has MFA set up, needs to enter code
          setMfaRequired(true)
          resolve() // Resolve to stop loading, UI will show code input
        },
      })
    })
  }

  const verifyMfaCode = async (code: string): Promise<void> => {
    const cognitoUser = cognitoUserRef.current
    if (!cognitoUser) {
      throw new Error('No authentication in progress')
    }

    return new Promise((resolve, reject) => {
      cognitoUser.sendMFACode(
        code,
        {
          onSuccess: (_session) => {
            cognitoUser.getUserAttributes((_err, attributes) => {
              const email = attributes?.find((a) => a.Name === 'email')?.Value || ''
              const name = attributes?.find((a) => a.Name === 'name')?.Value
              setUser({ email, name })
              setMfaRequired(false)
              cognitoUserRef.current = null
              resolve()
            })
          },
          onFailure: (err) => {
            reject(err)
          },
        },
        'SOFTWARE_TOKEN_MFA'
      )
    })
  }

  const completeMfaSetup = async (code: string): Promise<void> => {
    const cognitoUser = cognitoUserRef.current
    if (!cognitoUser) {
      throw new Error('No authentication in progress')
    }

    return new Promise((resolve, reject) => {
      cognitoUser.verifySoftwareToken(code, 'Authenticator', {
        onSuccess: (_session) => {
          // After setup, we need to set TOTP as preferred
          cognitoUser.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
            if (err) {
              console.error('Error setting MFA preference:', err)
            }
            cognitoUser.getUserAttributes((_err, attributes) => {
              const email = attributes?.find((a) => a.Name === 'email')?.Value || ''
              const name = attributes?.find((a) => a.Name === 'name')?.Value
              setUser({ email, name })
              setMfaSetupRequired(false)
              setMfaSetupData(null)
              cognitoUserRef.current = null
              resolve()
            })
          })
        },
        onFailure: (err) => {
          reject(err)
        },
      })
    })
  }

  const cancelMfa = () => {
    setMfaRequired(false)
    setMfaSetupRequired(false)
    setMfaSetupData(null)
    cognitoUserRef.current = null
  }

  const logout = () => {
    if (!userPool) return

    const cognitoUser = userPool.getCurrentUser()
    if (cognitoUser) {
      cognitoUser.signOut()
    }
    setUser(null)
    setMfaRequired(false)
    setMfaSetupRequired(false)
    setMfaSetupData(null)
  }

  const getToken = async (): Promise<string | null> => {
    // If auth is disabled, return a dummy token
    if (!config.features.authEnabled) {
      return 'dev-token'
    }

    if (!userPool) return null

    const cognitoUser = userPool.getCurrentUser()
    if (!cognitoUser) return null

    return new Promise((resolve) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null)
        } else {
          resolve(session.getIdToken().getJwtToken())
        }
      })
    })
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        mfaRequired,
        mfaSetupRequired,
        mfaSetupData,
        login,
        logout,
        getToken,
        verifyMfaCode,
        completeMfaSetup,
        cancelMfa,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
